import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Mail, Check } from 'lucide-react';

export const SubscribePage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { subscribe } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const referrerId = urlParams.get('ref');

      if (!referrerId) {
        throw new Error('Link de indicação inválido');
      }

      await subscribe(email, referrerId);
      setSuccess(true);
    } catch (err: any) {
      if (err.message.includes('400')) {
        setError('Ops! Parece que houve um pequeno contratempo. Por favor, tente novamente mais tarde ou use outro e-mail.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const shareUrl = `https://app.thenewscc.com.br/subscribe?ref=${email}`;
    window.location.href = shareUrl;
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
        <div className="w-full max-w-4xl flex flex-col lg:flex-row">
          <div className="flex-1 p-6">
            <div className="mb-8">
              <div className="bg-yellow-400 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-black dark:text-white" />
              </div>
              <div className="text-yellow-400 font-medium mb-2">the news ☕️</div>
              <h1 className="text-3xl font-bold mb-4 dark:text-white">
                você está dentro! 🎉
              </h1>
              <div className="space-y-4 text-gray-600 dark:text-gray-400 max-w-md">
                <p>
                  prepare seu café preferido (sem açúcar, lembra?) e aguarde nossa primeira newsletter chegar na sua caixa de entrada.
                </p>
                <p>
                  enquanto isso, que tal compartilhar com seus amigos?
                </p>
                <div className="pt-4">
                  <a
                    onClick={handleShare}
                    href="#"
                    className="bg-yellow-400 text-black dark:text-white px-6 py-3 rounded-lg font-medium hover:bg-yellow-500 transition-colors inline-block"
                  >
                    compartilhar
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Preview card for success state */}
          <div className="flex-1 p-6 hidden lg:block">
            <Card className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                    <img
                      src="/square_logo_favicon.png"
                      alt="Coffee Cup"
                      className="w-9 h-9"
                    />
                  </div>
                  <div>
                    <div className="text-yellow-400 font-medium">the news 📬</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">sua primeira newsletter está a caminho</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse w-3/4"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse w-full"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse w-5/6"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse w-4/5"></div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full w-24"></div>
                      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full w-32"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
      <div className="w-full max-w-4xl flex flex-col lg:flex-row">
        {/* Left section */}
        <div className="flex-1 p-6">
          <div className="mb-8">
            <div className="bg-yellow-400 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
              <img
                src="https://app.thenewscc.com.br/square_logo_favicon.png"
                alt="Coffee Cup"
                className="w-12 h-12"
              />
            </div>
            <div className="text-yellow-400 font-medium mb-2">the news</div>
            <h1 className="text-3xl font-bold mb-2 dark:text-white">
              mais inteligente
              <br />
              em 5 minutos ☕
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">
              tudo que você precisa saber pra começar seu dia bem e informado.
              <br />
              as principais notícias do Brasil e do mundo, 100% gratuito, diariamente na sua caixa de entrada.
              <br />
              perfeitas se acompanhadas de uma boa xícara de café (sem açúcar).
            </p>

            <form onSubmit={handleSubmit} className="mb-4">
              <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="coloque seu melhor e-mail"
                    className="pl-10 h-12 border-2 border-gray-200 rounded-lg"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="bg-yellow-400 text-black dark:text-white px-6 py-3 rounded-lg font-medium hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'Aguarde...' : 'inscreva-se'}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-red-500 text-sm">{error}</p>
              )}
            </form>

            <p className="text-yellow-400 font-medium mb-4">
              junte-se a +1.5M de leitores
            </p>

            <a
              href="https://thenewscc.beehiiv.com/"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              deixe-me ler primeiro →
            </a>
          </div>
        </div>

        {/* Right section - Preview cards */}
        <div className="flex-1 p-6 hidden lg:block">
          <Card className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                  <img
                    src="https://app.thenewscc.com.br/square_logo_favicon.png"
                    alt="Coffee Cup"
                    className="w-6 h-6"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800 dark:text-gray-200">the news</span>
                    <span className="text-sm text-gray-500">06:06</span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">para: você@email.com</div>
                </div>
              </div>

              <img
                src="https://app.thenewscc.com.br/email-header.jpg"
                alt="Header"
                className="object-cover rounded-t-lg rounded-b-none mb-6"
              />

              <div className="space-y-4">
                <h3 className="text-xl font-extrabold mb-2 dark:text-white">é pra ler com café</h3>
                <p className="text-gray-600 dark:text-gray-400">
                bom dia. talvez essa seja sua primeira edição. se for, que bom. qual foi a última vez que teve um primeiro encontro? pegue seu café e aproveite. talvez te surpreenda mais que o seu último date. risos.
                </p>
                
                <div className="mt-4">
                  <h3 className="text-lg font-extrabold mb-2 dark:text-white">
                    Quando a trama de Suits fica pequena perto da vida "Real"
                  </h3>
                  <p className="text-gray-600 font-extrabold text-xs mb-2">MUNDO</p>
                  <img
                    src="https://app.thenewscc.com.br/preview-image.png"
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}; 